import { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { toast } from 'sonner';
import type { IssueVerification, IssueStatus, Ticket } from '@overlayqa/shared';
import { newUuid, verificationHandoffUrl } from '@overlayqa/shared';
import { useUpdateIssue } from '../../hooks/useTickets';
import { useExtensionDetection } from '../../hooks/useExtensionDetection';
import { resolveDashboardVerifyHandoff } from './verifyHandoff';
import { isVerifyEnabled } from '../../lib/verifyFlag';
import { CHROME_EXTENSION_URL } from '../../lib/constants';
import { verifyApi } from '../../lib/api/verify';
import { commentsApi } from '../../lib/api/comments';
import { useAuth } from '../../lib/auth';
import { log } from '@/lib/logger';

// Statuses for which the VerificationCard is shown. Mirrors the extension's
// IssueModal logic (see packages/extension/src/content/components/IssueModal.tsx).
const VERIFY_STATUSES = new Set<IssueStatus>(['resolved', 'verified', 'closed']);

type UpdateIssueMutation = ReturnType<typeof useUpdateIssue>;

/**
 * ApiError (lib/api/client) carries a server `code` + human `message`. Read them
 * structurally instead of importing ApiError — client.ts uses `import.meta`,
 * which the dashboard's jest can't parse in this hook's unit test.
 */
function readApiError(err: unknown): { code?: string; message?: string } {
  if (typeof err === 'object' && err !== null) {
    const e = err as { code?: unknown; message?: unknown };
    return {
      code: typeof e.code === 'string' ? e.code : undefined,
      message: typeof e.message === 'string' ? e.message : undefined,
    };
  }
  return {};
}

export interface IssueVerificationApi {
  showVerification: boolean;
  latestVerification: IssueVerification | null;
  verificationHistory: IssueVerification[];
  historyState: 'loading' | 'ready' | 'error';
  onRetryHistory: () => void;
  /** Spinner state for the card while a server-side verify is in flight. */
  inProgress: { stage: 'capturing'; percent: number } | null;
  /** True when server-side capture failed and we offer the extension fallback. */
  serverCaptureFailed: boolean;
  onVerify: () => void;
  /** Force the extension handoff (fallback after server_capture_failed). */
  onVerifyViaExtension: () => void;
  onMarkVerified: () => Promise<void>;
  onReopen: () => Promise<void>;
  onSkip: () => void;
  /** Which status action (mark verified / reopen) is mid-flight, so the card
      can disable its buttons. onReopen runs up to 3 awaits (status PATCH +
      caveat comment); without this a double-click posted the comment twice. */
  statusActionPending: 'verify' | 'reopen' | null;
}

/**
 * Verification orchestration for the dashboard issue detail view. Extracted
 * from IssueDetailPage with no behavior change.
 *
 * Mirrors the extension IssueModal pattern but fetches via the dashboard's
 * verifyApi which expects (token, issueId). The card is mounted only when the
 * issue status ∈ resolved/verified/closed AND the verify feature flag is on
 * (on in prod since 2026-10-09); otherwise we never fetch.
 *
 * "Verify fix" re-captures a public URL on the server (`server_puppeteer`) and
 * hands any other page to the extension; mark/reopen flip status via the
 * shared mutation.
 */
export function useIssueVerification(
  ticket: Ticket | undefined,
  updateIssueMutation: UpdateIssueMutation,
  readOnly = false,
): IssueVerificationApi {
  const { getToken } = useAuth();
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const showVerification = ticket ? VERIFY_STATUSES.has(ticket.status) && isVerifyEnabled() : false;
  const [latestVerification, setLatestVerification] = useState<IssueVerification | null>(null);
  const [verificationHistory, setVerificationHistory] = useState<IssueVerification[]>([]);
  const [historyState, setHistoryState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [historyRetry, setHistoryRetry] = useState(0);
  // Server-side verify (public URLs): the card spins while the server re-captures
  // + checks; on a capture failure we offer the extension handoff instead.
  const [serverVerifying, setServerVerifying] = useState(false);
  const [serverCaptureFailed, setServerCaptureFailed] = useState(false);
  // Mark-verified / reopen busy state. The ref guards same-tick re-entry
  // (two clicks before React re-renders the disabled buttons); the state
  // drives the card's disabled/label treatment.
  const [statusActionPending, setStatusActionPending] = useState<'verify' | 'reopen' | null>(null);
  const statusActionInFlightRef = useRef(false);
  // A fresh scope on every issue selection prevents A → B → A from reviving
  // the first A request. Saving may finish; only this scope may paint its UI.
  const scope = useMemo(() => ({ active: true, verifying: false, history: 0 }), [ticket?.id]);
  useLayoutEffect(() => {
    scope.active = true;
    setLatestVerification(null);
    setVerificationHistory([]);
    setServerVerifying(false);
    setHistoryState('loading');
    setServerCaptureFailed(false);
    setStatusActionPending(null);
    statusActionInFlightRef.current = false;
    return () => { scope.active = false; scope.history++; };
  }, [scope]);
  // Public pages use the server. Local/private pages require the extension's
  // live authenticated page, which is also the server-capture fallback.
  const { detected: extensionDetected } = useExtensionDetection({ poll: true });

  useEffect(() => {
    if (!ticket || !showVerification) {
      setLatestVerification(null);
      setVerificationHistory([]);
      return;
    }
    let cancelled = false;
    const historyRequest = ++scope.history;
    setHistoryState('loading');
    log.info('[IssueDetailPage] Loading verifications', { issueId: ticket.id });
    (async () => {
      try {
        const token = await getToken();
        const r = await verifyApi.listVerifications(token ?? undefined, ticket.id);
        if (cancelled || !scope.active || historyRequest !== scope.history) return;
        setLatestVerification(r.verifications[0] ?? null);
        setVerificationHistory(r.verifications.slice(1));
        setHistoryState('ready');
      } catch (err: unknown) {
        if (cancelled || !scope.active || historyRequest !== scope.history) return;
        setHistoryState('error');
        log.warn('[IssueDetailPage] listVerifications failed', { err });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticket?.id, ticket?.status, showVerification, getToken, scope, historyRetry]);

  // Public URLs use server capture. Local/private pages open in the extension
  // with a handoff field that preserves the site's query and hash route.
  const onVerify = useCallback(async () => {
    if (readOnlyRef.current || !scope.active || scope.verifying) return;
    const decision = resolveDashboardVerifyHandoff({
      pageUrl: ticket?.pageUrl,
      issueId: ticket?.id ?? '',
      extensionDetected,
    });
    switch (decision.kind) {
      case 'no-page-url':
        toast.error('This issue has no captured page URL.');
        return;
      case 'extension-required':
        // Local/private page with no extension: it can only be re-captured on
        // the user's machine, so prompt an install.
        toast.error(
          'This looks like a local or private page. Verifying it needs the OverlayQA browser extension, which re-captures the page on your machine. Install it, then try again.',
        );
        window.open(CHROME_EXTENSION_URL, '_blank', 'noopener,noreferrer');
        return;
      case 'open-handoff':
        log.info('[IssueDetailPage] Opening single-issue verify handoff', { url: decision.url });
        window.open(decision.url, '_blank', 'noopener,noreferrer');
        return;
      case 'server-verify': {
        // Public URL: the server re-captures the page itself and runs the check.
        if (!ticket) return;
        scope.verifying = true;
        scope.history++; // an older history read cannot replace the new result
        setServerCaptureFailed(false);
        setServerVerifying(true);
        try {
          const token = await getToken();
          if (readOnlyRef.current || !scope.active) return;
          const result = await verifyApi.verifyIssue(token ?? undefined, ticket.id, {
            source: 'server_puppeteer',
            idempotencyKey: newUuid(),
          });
          if (!scope.active) return;
          scope.history++;
          // Surface the new verdict immediately; push the prior latest to history.
          setVerificationHistory((prev) => (latestVerification ? [latestVerification, ...prev] : prev));
          setLatestVerification(result);
          setHistoryState('ready');
          log.info('[IssueDetailPage] Server verify done', { issueId: ticket.id, status: result.status });
        } catch (err: unknown) {
          if (!scope.active) return;
          const api = readApiError(err);
          if (api.code === 'server_capture_failed') {
            log.info('[IssueDetailPage] Server capture failed; offering extension fallback', { issueId: ticket.id });
            setServerCaptureFailed(true);
          } else {
            log.error('[IssueDetailPage] Server verify failed', { err });
            toast.error(api.message ?? 'Verification failed. Please try again.');
          }
        } finally {
          scope.verifying = false;
          if (scope.active) setServerVerifying(false);
        }
        return;
      }
    }
  }, [ticket?.id, ticket?.pageUrl, extensionDetected, getToken, latestVerification, scope]);

  // Fallback when server-side capture fails (login wall / unreachable): force the
  // extension handoff for this issue regardless of the public-URL route.
  const onVerifyViaExtension = useCallback(() => {
    if (readOnlyRef.current || !ticket?.pageUrl) return;
    setServerCaptureFailed(false);
    if (!extensionDetected) {
      window.open(CHROME_EXTENSION_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    window.open(verificationHandoffUrl(ticket.pageUrl, 'issue', ticket.id), '_blank', 'noopener,noreferrer');
  }, [ticket?.id, ticket?.pageUrl, extensionDetected]);

  const onMarkVerified = useCallback(async () => {
    if (readOnlyRef.current || !ticket || statusActionInFlightRef.current) return;
    statusActionInFlightRef.current = true;
    setStatusActionPending('verify');
    try {
      await updateIssueMutation.mutateAsync({
        id: ticket.id,
        data: { status: 'verified' },
      });
    } catch (err: unknown) {
      log.error('[IssueDetailPage] Mark verified failed', { err });
      if (scope.active) toast.error('Could not mark issue verified.');
    } finally {
      if (scope.active) {
        statusActionInFlightRef.current = false;
        setStatusActionPending(null);
      }
    }
  }, [ticket?.id, updateIssueMutation, scope]);

  const onReopen = useCallback(async () => {
    if (readOnlyRef.current || !ticket || !latestVerification || statusActionInFlightRef.current) return;
    statusActionInFlightRef.current = true;
    setStatusActionPending('reopen');
    try {
      if (latestVerification.status === 'failed') {
        await updateIssueMutation.mutateAsync({
          id: ticket.id,
          data: { status: 'open', regressed: true },
        });
      } else if (latestVerification.status === 'passed_with_caveat') {
        await updateIssueMutation.mutateAsync({
          id: ticket.id,
          data: { status: 'in-progress' },
        });
        if (latestVerification.explanation) {
          let posting = false;
          let posted = false;
          const retryComment = async () => {
            if (posting || posted) return;
            posting = true;
            try {
              const token = await getToken();
              if (!token) throw new Error('Sign in to post the caveat comment');
              await commentsApi.create(token, ticket.id, {
                content: `AI caveat: ${latestVerification.explanation}`, mentionedUserIds: [],
              }, { idempotencyKey: `verify-caveat-${latestVerification.id}` });
              posted = true;
            } catch (err: unknown) {
              log.warn('[IssueDetailPage] Failed to post caveat comment', { err });
              if (scope.active) toast.error('Issue reopened, but the caveat comment could not be posted.', {
                duration: 10000,
                action: { label: 'Retry comment', onClick: () => { void retryComment(); } },
              });
            } finally { posting = false; }
          };
          await retryComment();
        }
      } else {
        await updateIssueMutation.mutateAsync({
          id: ticket.id,
          data: { status: 'open' },
        });
      }
    } catch (err: unknown) {
      log.error('[IssueDetailPage] Reopen failed', { err });
      if (scope.active) toast.error('Could not reopen issue.');
    } finally {
      if (scope.active) {
        statusActionInFlightRef.current = false;
        setStatusActionPending(null);
      }
    }
  }, [ticket?.id, latestVerification, updateIssueMutation, getToken, scope]);

  const onSkip = useCallback(() => {
    // No-op: clear the local card until the next verification.
    setLatestVerification(null);
  }, []);

  return {
    showVerification,
    latestVerification,
    verificationHistory,
    historyState,
    onRetryHistory: () => setHistoryRetry(value => value + 1),
    inProgress: serverVerifying ? { stage: 'capturing', percent: 50 } : null,
    serverCaptureFailed,
    onVerify,
    onVerifyViaExtension,
    onMarkVerified,
    onReopen,
    onSkip,
    statusActionPending,
  };
}
